/**
 * Unified utility functions for building tournament player data
 * Combines round standings with tournament.players data for consistent player information
 */

import { isPlayerInRound } from "vue-chess-shared/tournament/utils/playerUtils";

/**
 * Build complete player data by combining round standings with tournament players
 * @param {Object} round - Tournament round object
 * @param {Array} tournamentPlayers - Array of tournament players
 * @param {Function} isPaired - Function to check if player is paired
 * @param {Function} getColorPref - Function to get color preference
 * @returns {Array} Array of complete player standings
 */
export function buildCompletePlayerStandings(
  round,
  tournamentPlayers,
  isPaired = null,
  getColorPref = null
) {
  const roundStandings = round.standings || [];

  // If no round standings exist (first round), create initial standings from tournament players
  if (roundStandings.length === 0) {
    let standings = tournamentPlayers
      .filter((player) => !isPaired || !isPaired(player.username))
      .map((player) => ({
        player: {
          id: player.username,
          username: player.username,
          matches: [],
          user: player.user,
        },
        matchPoints: player.score || 0,
        wins: 0,
        draws: 0,
        losses: 0,
        rank: player.currentRank || 0,
        // Add full player data for easy access
        fullPlayer: player,
        // Calculate color preference if function provided
        colorPref: getColorPref ? getColorPref(round, player) : "+0",
      }));

    // For round 1, sort participated players first, then by rank
    if (round.roundNumber === 1) {
      standings.sort((a, b) => {
        // FIXME: confirm the order logic for the first round
        const aParticipated = isPlayerInRound(a.fullPlayer, round.roundNumber)
          ? 1
          : 0;
        const bParticipated = isPlayerInRound(b.fullPlayer, round.roundNumber)
          ? 1
          : 0;

        // Participated players come first
        if (aParticipated && !bParticipated) return -1;
        if (!aParticipated && bParticipated) return 1;

        // If both have same participation status, sort by rank (lower rank number = better rank)
        return (
          (a.fullPlayer.currentRank || Infinity) -
          (b.fullPlayer.currentRank || Infinity)
        );
      });
    }

    return standings;
  }

  // Combine round standings with tournament player data
  return roundStandings
    .filter((standing) => !isPaired || !isPaired(standing.player.id))
    .map((standing) => {
      // Find the corresponding tournament player
      const tournamentPlayer = tournamentPlayers.find(
        (p) =>
          p.username === standing.player.id ||
          p.username === standing.player.username
      );

      const fullPlayer = tournamentPlayer || {
        username: standing.player.id,
        user: standing.player.user,
        score: standing.matchPoints,
        currentRank: standing.rank,
      };

      return {
        ...standing,
        player: {
          ...standing.player,
          // Ensure user data is available from tournament player
          user: tournamentPlayer?.user || standing.player.user,
        },
        // Add full player data for easy access
        fullPlayer: fullPlayer,
        // Calculate color preference if function provided
        colorPref: getColorPref ? getColorPref(round, fullPlayer) : "+0",
      };
    });
}

/**
 * Create a complete player standing object for a single player
 * @param {Object} player - Player object
 * @param {Object} round - Tournament round object
 * @param {Array} tournamentPlayers - Array of tournament players
 * @param {Function} getColorPref - Function to get color preference
 * @returns {Object} Complete standing object
 */
export function createCompletePlayerStanding(
  player,
  round,
  tournamentPlayers,
  getColorPref = null
) {
  if (!player) return null;

  // Try to find the player in round standings
  const roundStanding = round.standings?.find(
    (s) =>
      s.player.id === player.username || s.player.username === player.username
  );

  // Find the tournament player data
  const tournamentPlayer = tournamentPlayers.find(
    (p) => p.username === player.username
  );

  return {
    player: {
      id: player.username,
      username: player.username,
      matches: roundStanding?.player.matches || [],
      user: tournamentPlayer?.user || player.user,
    },
    matchPoints:
      roundStanding?.matchPoints ||
      tournamentPlayer?.score ||
      player.score ||
      0,
    wins: roundStanding?.wins || 0,
    draws: roundStanding?.draws || 0,
    losses: roundStanding?.losses || 0,
    rank:
      roundStanding?.rank ||
      tournamentPlayer?.currentRank ||
      player.currentRank ||
      0,
    // Add full player data for easy access
    fullPlayer: tournamentPlayer || player,
    // Calculate color preference if function provided
    colorPref: getColorPref
      ? getColorPref(round, tournamentPlayer || player)
      : "+0",
  };
}

/**
 * Find a player by username in tournament players array
 * @param {Array} tournamentPlayers - Array of tournament players
 * @param {string} username - Username to find
 * @returns {Object|null} Player object or null if not found
 */
export function findTournamentPlayer(tournamentPlayers, username) {
  if (!username || !tournamentPlayers) return null;
  return tournamentPlayers.find((p) => p.username === username) || null;
}
